import * as THREE from "three";
import { store } from "../core/store.js";

const SCENE_STATE_PROPERTIES = ["visible"];

class GridSystem {
  constructor() {
    this.gridObject = null;
    this.lightTexture = null;
    this.darkTexture = null;
    this.material = null;
    this.state = {
      visible: true,
      enabled: true,
      colorMode: "light",
      scale: 1.0,
      tiling: 500,
      offsetX: 0,
      offsetY: 0,
    };
    this.subscribers = new Set();
    this.texturesLoaded = { light: false, dark: false };
  }

  setInitialLoadedState(gridData) {
    this.initialLoadedState = gridData ? { ...gridData } : null;
  }

  setVisibleStartFromCurrentState() {
    const currentState = this.getState();
    const initialState = this.getInitialLoadedState() || this.serialize();
    this.setInitialLoadedState({
      ...initialState,
      visible: currentState.visible,
    });
  }

  showVisibleStart() {
    const initialGridData = this.getInitialLoadedState();

    if (!initialGridData) {
      console.warn("No initial grid data available to reset.");
      return;
    }

    this.deserialize(initialGridData);
  }

  getInitialLoadedState() {
    return this.initialLoadedState ? { ...this.initialLoadedState } : null;
  }

  getInitialLoadedVisibility() {
    if (typeof this.initialLoadedState?.visible === "boolean") {
      return this.initialLoadedState.visible;
    }

    return true;
  }

  create(scene) {
    const geometry = new THREE.PlaneGeometry(40000, 40000, 1000, 1000);

    // Preload both textures
    this.loadTextures();

    this.material = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      metalness: 0.0,
      roughness: 1.0,
      side: THREE.FrontSide,
      map: this.lightTexture, // Start with light texture
    });

    this.gridObject = new THREE.Mesh(geometry, this.material);
    this.gridObject.receiveShadow = true;
    this.gridObject.position.set(0, -2.5, 0);
    this.gridObject.rotation.x = -Math.PI / 2;
    this.gridObject.visible = this.state.visible;
    scene.add(this.gridObject);
    return this;
  }

  loadTextures() {
    const loader = new THREE.TextureLoader();

    // Load light texture
    this.lightTexture = loader.load("/static/img/gridfloor_white.png", () => {
      this.setupTexture(this.lightTexture);
      this.texturesLoaded.light = true;
      this.notifyChange("textureLoaded");
    });

    // Load dark texture
    this.darkTexture = loader.load("/static/img/gridfloor_gray.png", () => {
      this.setupTexture(this.darkTexture);
      this.texturesLoaded.dark = true;
      this.notifyChange("textureLoaded");
    });
  }

  setupTexture(texture) {
    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(this.state.tiling, this.state.tiling);
  }

  // Simple visibility toggle
  setVisible(visible) {
    this.state.visible = Boolean(visible);
    if (this.gridObject) {
      this.gridObject.visible = this.state.visible;
    }
    this.notifyChange("visibility");
  }

  setColorMode(mode) {
    if (mode !== "light" && mode !== "dark") return;

    this.state.colorMode = mode;

    if (this.material) {
      // Switch textures immediately - no async loading needed
      const newTexture = mode === "light" ? this.lightTexture : this.darkTexture;
      this.material.map = newTexture;
      this.material.needsUpdate = true;
      this.notifyChange("colorMode");
    }
  }

  // Simple tiling update
  setTiling(tiling) {
    const numTiling = Number(tiling);
    if (isNaN(numTiling) || numTiling <= 0) return;

    this.state.tiling = numTiling;

    // Update both textures
    if (this.lightTexture) {
      this.lightTexture.repeat.set(numTiling, numTiling);
    }
    if (this.darkTexture) {
      this.darkTexture.repeat.set(numTiling, numTiling);
    }

    this.notifyChange("tiling");
    store.requestRender();
  }

  // Simple scale
  setScale(scale) {
    const numScale = Number(scale);
    if (isNaN(numScale) || numScale <= 0) return;

    this.state.scale = numScale;
    if (this.gridObject) {
      this.gridObject.scale.set(numScale, numScale, numScale);
    }
    this.notifyChange("scale");
    store.requestRender();
  }

  // Get current state
  getState() {
    return { ...this.state };
  }

  // For scene resets - clean GPU resources but keep subscribers
  resetForNewScene() {
    if (this.gridObject && this.gridObject.parent) {
      this.gridObject.parent.remove(this.gridObject);
    }

    if (this.lightTexture) this.lightTexture.dispose();
    if (this.darkTexture) this.darkTexture.dispose();
    if (this.material) this.material.dispose();
    if (this.gridObject?.geometry) this.gridObject.geometry.dispose();

    // Reset object references but keep subscribers
    this.gridObject = null;
    this.lightTexture = null;
    this.darkTexture = null;
    this.material = null;
  }

  // Subscribe to changes
  subscribe(callback) {
    if (typeof callback === "function") {
      this.subscribers.add(callback);
      return () => this.subscribers.delete(callback);
    }
  }

  notifyChange(changeType) {
    this.subscribers.forEach((callback) => {
      try {
        callback({ type: changeType, state: this.getState() });
      } catch (error) {
        console.error("Grid subscriber error:", error);
      }
    });
  }

  // For complete app shutdown - clean everything including subscribers
  dispose() {
    this.resetForNewScene();
    this.subscribers.clear(); // Only clear when truly disposing
  }

  // Serialization for scene saving (if needed)
  serialize() {
    return {
      visible: this.state.visible,
      colorMode: this.state.colorMode,
      scale: this.state.scale,
      tiling: this.state.tiling,
      offsetX: this.state.offsetX,
      offsetY: this.state.offsetY,
    };
  }

  serializeSceneState() {
    const state = this.getState();
    return SCENE_STATE_PROPERTIES.reduce((sceneState, prop) => {
      if (Object.prototype.hasOwnProperty.call(state, prop)) {
        sceneState[prop] = state[prop];
      }
      return sceneState;
    }, {});
  }

  deserializeSceneState(data) {
    if (data && typeof data.visible === "boolean") {
      this.setVisible(data.visible);
    } else {
      this.setVisible(this.getInitialLoadedVisibility());
    }
  }

  // Load from saved data
  deserialize(data) {
    if (!data) return;

    this.setVisible(data.visible ?? true);
    this.setColorMode(data.colorMode ?? "light");
    this.setScale(data.scale ?? 1.0);
    this.setTiling(data.tiling ?? 500);

    this.state.offsetX = data.offsetX ?? 0;
    this.state.offsetY = data.offsetY ?? 0;
  }
}

export const grid = new GridSystem();
